import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const FeedbackQueries = () => {
  const [activeTab, setActiveTab] = useState('feedback');
  const [feedback, setFeedback] = useState([]);
  const [queries, setQueries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState({
    feedback: true,
    queries: true,
    stats: true,
  });
  const [filters, setFilters] = useState({
    minRating: 0,
    status: 'all',
  });
  const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

  // Fetch data from APIs
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch feedback
        const feedbackRes = await fetch(
          `${BQ_BASE_URL}/bq/api/feedback/?min_rating=${filters.minRating}`
        );
        const feedbackData = await feedbackRes.json();
        setFeedback(feedbackData);
        setLoading((prev) => ({ ...prev, feedback: false }));

        // Fetch queries
        const queriesRes = await fetch(`${BQ_BASE_URL}/bq/api/need-help/?skip=0&limit=100`);
        const queriesData = await queriesRes.json();
        setQueries(queriesData.queries);
        setLoading((prev) => ({ ...prev, queries: false }));

        // Fetch stats
        const statsRes = await fetch(`${BQ_BASE_URL}/bq/api/feedback/stats/ratings`);
        const statsData = await statsRes.json();
        setStats(statsData);
        setLoading((prev) => ({ ...prev, stats: false }));
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading({ feedback: false, queries: false, stats: false });
      }
    };

    fetchData();
  }, [filters.minRating]);

  const updateFeedbackStatus = async (feedbackId, status) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/feedback/${feedbackId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        setFeedback((prev) =>
          prev.map((item) => (item.feedback_id === feedbackId ? { ...item, status } : item))
        );
      }
    } catch (error) {
      console.error('Error updating feedback status:', error);
    }
  };

  const filteredFeedback = feedback.filter(
    (item) => filters.status === 'all' || item.status === filters.status
  );

  const ratingChartData = stats
    ? [
        { name: 'Overall', value: stats.average_ratings.overall },
        { name: 'Cleanliness', value: stats.average_ratings.cleanliness },
        { name: 'Value', value: stats.average_ratings.value_for_money },
        { name: 'Service', value: stats.average_ratings.service },
        { name: 'Location', value: stats.average_ratings.location },
      ]
    : [];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">PMS Admin Dashboard</h1>
        <p className="text-gray-600">Manage guest feedback and queries</p>
      </header>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main Content Area */}
        <div className="flex-1">
          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="flex border-b border-gray-200">
              <button
                className={`px-4 py-3 font-medium text-sm ${activeTab === 'feedback' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('feedback')}
              >
                Guest Feedback
              </button>
              <button
                className={`px-4 py-3 font-medium text-sm ${activeTab === 'queries' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('queries')}
              >
                Guest Queries
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-4">
              {activeTab === 'feedback' ? (
                <div>
                  {/* Feedback Filters */}
                  <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Rating
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={filters.minRating}
                        onChange={(e) =>
                          setFilters({ ...filters, minRating: Number(e.target.value) })
                        }
                      >
                        <option value={0}>All Ratings</option>
                        <option value={3}>3+ Stars</option>
                        <option value={4}>4+ Stars</option>
                        <option value={5}>5 Stars</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      >
                        <option value="all">All Statuses</option>
                        <option value="new">New</option>
                        <option value="acknowledged">Acknowledged</option>
                        <option value="resolved">Resolved</option>
                      </select>
                    </div>
                  </div>

                  {/* Feedback List */}
                  {loading.feedback ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  ) : filteredFeedback.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No feedback matching your filters
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredFeedback.map((item) => (
                        <div
                          key={item.feedback_id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-medium">{item.name}</h3>
                              <p className="text-sm text-gray-600">{item.email}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(item.submission_date).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  item.overall_rating >= 4
                                    ? 'bg-green-100 text-green-800'
                                    : item.overall_rating >= 2
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {item.overall_rating} ★
                              </span>
                              <select
                                className="border border-gray-300 rounded px-2 py-1 text-xs"
                                value={item.status || 'new'}
                                onChange={(e) =>
                                  updateFeedbackStatus(item.feedback_id, e.target.value)
                                }
                              >
                                <option value="new">New</option>
                                <option value="acknowledged">Acknowledged</option>
                                <option value="resolved">Resolved</option>
                              </select>
                            </div>
                          </div>

                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700">Feedback:</p>
                            <p className="text-sm text-gray-600 mt-1">
                              {item.message || 'No message provided'}
                            </p>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-2">
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                item.would_recommend
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {item.would_recommend ? 'Would recommend' : 'Would not recommend'}
                            </span>
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                item.would_return
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {item.would_return ? 'Would return' : 'Would not return'}
                            </span>
                            {item.is_contact_allowed && (
                              <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                Contact allowed
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {/* Queries List */}
                  {loading.queries ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  ) : queries.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No guest queries found</div>
                  ) : (
                    <div className="space-y-3">
                      {queries.map((query) => (
                        <div
                          key={query.id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-medium">{query.name}</h3>
                              <p className="text-sm text-gray-600">{query.email}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(query.created_at).toLocaleString()}
                              </p>
                            </div>
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                query.query_type === 'booking'
                                  ? 'bg-purple-100 text-purple-800'
                                  : query.query_type === 'payment'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {query.query_type}
                            </span>
                          </div>

                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700">Query:</p>
                            <p className="text-sm text-gray-600 mt-1">{query.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-80 space-y-6">
          {/* Statistics Card */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="font-medium text-gray-800 mb-4">Feedback Statistics</h3>

            {loading.stats ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : stats ? (
              <div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium">Total Feedback</p>
                    <p className="text-xl font-bold">{stats.total_feedbacks}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-green-600 font-medium">Recommendation Rate</p>
                    <p className="text-xl font-bold">{stats.recommendation_rate}</p>
                  </div>
                </div>

                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ratingChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, 5]} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No statistics available</p>
            )}
          </div>

          {/* Quick Actions Card */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="font-medium text-gray-800 mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm font-medium transition">
                Export Feedback Data
              </button>
              <button className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm font-medium transition">
                Send Bulk Response
              </button>
              <button className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded text-sm font-medium transition">
                Generate Monthly Report
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackQueries;
